import logger from '@/modules/Logger';
import {
  BANDWIDTH_THRESHOLDS,
  BUFFER_SWITCH_MIN_COUNT,
  BUFFER_ACTIVATE_AUDIO_ONLY,
  BUFFER_DEACTIVATE_AUDIO_ONLY,
  STREAM_TYPE
} from '@/modules/webrtc/agora/const';
import store from '@/store/index';
import _ from 'lodash';

let newDownlinkQuality = {};

export default {
  async setQualityOnDownLinkSpeed(quality) {
    newDownlinkQuality = this.setQualityOfNetwork(quality);

    if (this.downLinkNetworkQuality === newDownlinkQuality.value) {
      return;
    }

    if (this.isWithinBufferLimit()) {
      this.switchDownLinkBuffer.push(newDownlinkQuality.value);
      return;
    }

    // activate lowest quality based on the accumulated data while in buffer mode
    if (this.isDownLinkPoorQuality()) {
      const bufferQuality = _.max(this.switchDownLinkBuffer);
      newDownlinkQuality = this.setQualityOfNetwork(bufferQuality);
    }

    await this.setReceiveStreamQuality();
  },

  async setReceiveStreamQuality() {
    this.switchDownLinkBuffer = [];
    this.downLinkNetworkQuality = newDownlinkQuality.value;

    if (!this.rtc.remoteUsers.length || !this.rtc.remoteUsers[0].hasVideo) {
      logger.debug('No remote user on the channel or remote user has no video track');
      return;
    }

    logger.info('[Agora] Downlink network changes, switch receive stream quality', {
      operation: 'webrtc:call-quality',
      quality: newDownlinkQuality.label
    });

    try {
      if (this.isDownLinkPoorQuality() && process.env.VUE_APP_AGORA_ENABLE_AUDIO_ONLY === 'true') {
        logger.error('Detected downlink network quality is poor', {
          quality: newDownlinkQuality.label,
          event_type: 49,
          lesson_id: store.state.lessonId
        });
        await this.switchToAudioOnly();
      } else {
        await this.adjustRemoteVideoQuality();
      }
    } catch (error) {
      logger.error('[Agora] Error while setting receive stream quality', {
        operation: 'webrtc:call-quality',
        error: error.message,
        event_type: 49,
        lesson_id: this.lessonId
      });
    }
  },

  // on poor network quality, switch to AUDIO_ONLY
  async switchToAudioOnly() {
    logger.info('[Agora] Switch to AUDIO ONLY');
    await this.rtc.unsubscribe(this.rtc.remoteUsers[0], 'video');
    store.commit('videoRtc/setAudioOnly', true);
  },

  // on excellent or moderate network quality, adjust remote video stream resolution
  async adjustRemoteVideoQuality() {
    const remoteUser = this.rtc.remoteUsers[0];
    const wasAudioOnly = store.state.videoRtc.audioOnly;

    await this.rtc.subscribe(remoteUser, 'video');

    // After recovering from audio-only, the previous unsubscribe ended the old MediaStreamTrack.
    // Rebuild the MediaStream with the fresh track so the <video> element renders it.
    if (wasAudioOnly && remoteUser.videoTrack) {
      const uid = remoteUser.uid;

      // Update remoteUserTrack with the fresh video track (matching user-published behavior)
      this.remoteUserTrack = this.remoteUserTrack.filter(item => item.trackMediaType !== 'video');
      this.remoteUserTrack.push(remoteUser.videoTrack);

      // Remove old entry and rebuild stream from all current tracks (new instance so Vue re-assigns srcObject)
      this.remoteUserExists(uid);
      const remoteStream = new MediaStream();
      this.remoteUserTrack.forEach(track => {
        const mediaTrack = track.getMediaStreamTrack();
        if (mediaTrack) {
          remoteStream.addTrack(mediaTrack);
        }
      });
      this.remoteUsers.push({ peerId: uid, stream: remoteStream });

      logger.info('[Agora] Rebuilt remote stream after audio-only recovery', {
        operation: 'webrtc:call-quality',
        lesson_id: this.lessonId
      });
    }

    // Lift audio-only overlay after the stream is rebuilt to avoid showing a black frame
    store.commit('videoRtc/setAudioOnly', false);

    const videoQuality = this.isDownLinkExcellentQuality() ? STREAM_TYPE.high : STREAM_TYPE.low;
    await this.rtc.setRemoteVideoStreamType(this.remoteUserId, videoQuality);
  },

  /*
   * Apply buffering to minimize frequent switching.
   */
  isWithinBufferLimit() {
    if (newDownlinkQuality.value === BANDWIDTH_THRESHOLDS.poor.value) {
      return this.switchDownLinkBuffer.length <= BUFFER_ACTIVATE_AUDIO_ONLY;
    }

    // Recover faster when coming from audio-only mode
    const limit = this.isDownLinkPoorQuality()
      ? BUFFER_DEACTIVATE_AUDIO_ONLY
      : BUFFER_SWITCH_MIN_COUNT;

    return this.switchDownLinkBuffer.length <= limit;
  }
};
